import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, session } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Settings } from "lucide-react";
import { TEMPLATE_PRESETS, DATA_SOURCE_TERMS } from "@/components/tournament-setup/constants";
import TemplatePicker from "@/components/tournament-setup/TemplatePicker";
import { DataFeedTermsModal, RedeemTrialCodeModal } from "@/components/tournament-setup/SetupModals";
import { CustomTournamentFields } from "@/components/tournament-setup/CustomTournamentFields";
import { setStoredViewTournament } from "@/components/dashboard/TournamentSwitcher";
import Takeover from "@/components/onui/Takeover";


export default function TournamentSetup() {
    const { leagueId } = useParams();
    const navigate = useNavigate();
    // iter63z (wizard pre-fill) — AdminFirstRunWizard hands off to this
    // page via `?template=…&teams_per=…`. We seed state from those params
    // before the user sees the form. URL params win over defaults, and
    // win over the league-row default for teams_per_participant since
    // the admin explicitly chose them in the wizard one click ago.
    const [searchParams] = useSearchParams();
    const initialTemplateParam = searchParams.get("template");
    const initialTeamsPerParam = parseInt(searchParams.get("teams_per") || "", 10);
    // iter87.28 — Do NOT default to "world_cup_api" when no template was
    // passed. That silent default let admins press Create Tournament
    // without ever selecting a card and end up with a World Cup roster.
    // Empty string means "nothing selected"; the Create button and
    // handleCreate guard below enforce it.
    const initialTemplate = initialTemplateParam && TEMPLATE_PRESETS.some((t) => t.key === initialTemplateParam || (t.leagues || []).some((l) => l.key === initialTemplateParam))
        ? initialTemplateParam
        : "";
    // iter86.31.6 — Curated club-season bindings (Product Ops → Templates).
    const [clubBindings, setClubBindings] = useState(null);
    // iter86.45 — Curated golf event bindings + selected event (consolidated
    // picker; adds the event dropdown that only the wizard had before).
    const [golfBindings, setGolfBindings] = useState(null);
    const [golfBindingId, setGolfBindingId] = useState(null);
    // iter87.554 — Squares' real-game picker moved into the wizard itself
    // (Step 4, right after League) per operator decision — no more
    // post-creation "attach a game" admin tab. Fetched live once the
    // league (NFL/CFB) is resolved from the chosen template.
    const [squaresGames, setSquaresGames] = useState([]);
    const [squaresGamesLoading, setSquaresGamesLoading] = useState(false);
    const [squaresGameId, setSquaresGameId] = useState(null);
    const [tournamentName, setTournamentName] = useState("");
    // iter86.16 club-league timebox — declaration restored (iter86.29.2
    // no-undef guard caught the missing state: render crash on club leagues).
    const [seasonWindow, setSeasonWindow] = useState("full");
    // iter87.199.178 — Matchday Boost / Weekly Spread Challenge are now
    // always ON (server default) — the wizard checkbox was removed;
    // no client-side toggle state needed anymore.
    // iter87.199.79 — NFL Weekly picks per week.
    // Default 8 (operator lock 2026-02-16); admin can override 1..25.
    // iter87.199.164 — Seeded from initialTemplate (not a hardcoded 8) so a
    // deep-link straight into ?template=epl_boost_api/la_liga_boost_api
    // (e.g. from the marketing links) defaults to 5, matching the backend,
    // instead of relying solely on the template-change effect below.
    const [nflPicksPerRound, setNflPicksPerRound] = useState(
        initialTemplate === "epl_boost_api" || initialTemplate === "la_liga_boost_api" ? 5 : 8
    );
    const [template, setTemplate] = useState(initialTemplate);
    // iter87.199.163 — The soccer Weekly Spread card (EPL/La Liga) shares
    // this state with the NFL wizard, but its own default is 5 (mirrors
    // the backend). Snap to the right default whenever the template
    // switches INTO one of the 3 templates that use this field.
    const boostTplRef = useRef(initialTemplate);
    useEffect(() => {
        if (template === boostTplRef.current) return;
        if (template === "nfl_boost_api") setNflPicksPerRound(8);
        else if (template === "epl_boost_api" || template === "la_liga_boost_api") setNflPicksPerRound(5);
        boostTplRef.current = template;
    }, [template]);
    // iter87.554 — Squares game picker: refetch the curated shortlist
    // whenever the resolved league (NFL vs CFB) changes, and clear any
    // previously-picked game when the admin switches league/template
    // entirely (a Super Bowl pick shouldn't silently survive a switch
    // to CFB).
    const squaresSport = template.startsWith("nfl_squares_") ? "nfl" : template.startsWith("cfb_squares_") ? "cfb" : null;
    const squaresNeedsGame = !!squaresSport && !squaresGameId;
    const squaresSportRef = useRef(squaresSport);
    useEffect(() => {
        if (squaresSport === squaresSportRef.current) return;
        squaresSportRef.current = squaresSport;
        setSquaresGameId(null);
        if (!squaresSport) { setSquaresGames([]); return; }
        setSquaresGamesLoading(true);
        api.squaresCuratedGamesWizard(squaresSport)
            .then((d) => setSquaresGames(d.games || []))
            .catch(() => setSquaresGames([]))
            .finally(() => setSquaresGamesLoading(false));
    }, [squaresSport]);
    const [format, setFormat] = useState("group_knockout");
    const [knockoutSize, setKnockoutSize] = useState(8);
    // iter83.5 — Explicit data source for custom tournaments. Ignored on
    // pre-built templates (they always use the platform's contracted feeds).
    // CSV is exposed in the picker but disabled until the upload UI ships.
    const [dataSource, setDataSource] = useState("manual");
    // iter84.4 (Phase 2) — Per-feed Terms-of-Use acceptance gate. Each
    // non-manual feed must be explicitly accepted by the league admin
    // before it can be picked. Persisted only in component state — the
    // confirmation is logged as part of the tournament creation (the
    // chosen `data_source` is the binding commitment).
    const [termsAcceptedFor, setTermsAcceptedFor] = useState({});
    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [pendingDataSource, setPendingDataSource] = useState(null);
    // iter84.9 — Tournament date range. Required for new custom
    // tournaments (drives the GotSport auto-sync poller window). Stored
    // as YYYY-MM-DD strings to match the backend Tournament model.
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const dateRangeDays = (() => {
        if (!startDate || !endDate) return 0;
        const s = new Date(startDate); const e = new Date(endDate);
        if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
        return Math.max(0, Math.round((e - s) / 86400000));
    })();
    const [customTeams, setCustomTeams] = useState([{ name: "", group: "A" }, { name: "", group: "A" }]);
    const [loading, setLoading] = useState(false);
    const [league, setLeague] = useState(null);
    // iter86.74 — Custom Template Features: global availability (site admin
    // governed) + the league admin's Portfolio Draft choice for this rivalry.
    const [customFeaturesDisabled, setCustomFeaturesDisabled] = useState([]);
    const [portfolioUnlock, setPortfolioUnlock] = useState(false);
    // iter72 — trial-code upgrade modal triggered when backend returns
    // tournament_cap_reached with upgrade_via_trial_code=true.
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [upgradeMessage, setUpgradeMessage] = useState("");
    // iter31n — site-admin-controlled template disable list.
    const [disabledTemplates, setDisabledTemplates] = useState([]);
    // iter63z (template-visibility) — Site-Admin-controlled state per
    // template/sport key. Overrides override the hardcoded `available`
    // default in SPORTS / TEMPLATE_PRESETS:
    //   "hidden"      → card not rendered at all
    //   "coming_soon" → card grayed-out with "Coming soon" badge
    //   "live"        → card selectable as today
    // Missing key → use hardcoded default. Shared key namespace between
    // SPORTS and TEMPLATE_PRESETS (e.g. "olympics" controls both).
    const [visibilityOverrides, setVisibilityOverrides] = useState({});
    // iter31n — moved from Landing's "Request League" form. Default 2 unless
    // the league already has a non-default value (re-creating a tournament).
    // iter63z (wizard pre-fill) — if the AdminFirstRunWizard passed
    // ?teams_per=N, that takes precedence over the default.
    const [, setTeamsPerPlayer] = useState(
        Number.isInteger(initialTeamsPerParam) && initialTeamsPerParam > 0 ? initialTeamsPerParam : 2
    );

    useEffect(() => {
        const s = session.load();
        if (!s || s.league_id !== leagueId) {
            toast.error("Session expired");
            navigate("/");
            return;
        }
        api.getLeague(leagueId).then(async (d) => {
            setLeague(d.league);
            // Seed the dropdown from the league's current value (defaults to 2)
            // UNLESS the wizard handed us an explicit teams_per via URL param,
            // in which case the admin's just-now choice wins.
            if (d.league?.teams_per_participant && !(Number.isInteger(initialTeamsPerParam) && initialTeamsPerParam > 0)) {
                setTeamsPerPlayer(d.league.teams_per_participant);
            }
            // iter45b — Only bounce back when the league has a tournament AND
            // the admin has no capacity to add another. Otherwise let them
            // through to create a 2nd / 3rd / ... tournament. M2/M3 unlock this.
            if (d.tournament) {
                try {
                    const lst = await api.listLeagueTournaments(leagueId);
                    if (!lst?.can_create_more) {
                        toast.message("This rivalry is at its active-tournament limit", {
                            description: `${lst?.active_count || 1} / ${lst?.cap || 1} active. Complete a tournament or upgrade your tier.`,
                        });
                        navigate(`/league/${leagueId}`);
                    }
                    // else: capacity exists — stay on setup so they can create another
                } catch {
                    // Fallback to legacy single-tournament behavior on error
                    navigate(`/league/${leagueId}`);
                }
            }
        }).catch(() => {
            // iter87.213 punch-list Issue 5 — league/fixture was deleted (404)
            // out from under this admin (e.g. archived by site admin while
            // they had the setup page open). Bounce home instead of leaving
            // an unhandled rejection + a dead loading state.
            toast.error("This rivalry no longer exists");
            navigate("/");
        });
        // Fetch global template-disable list (public endpoint).
        api.getSiteSettings()
            .then((r) => setDisabledTemplates(Array.isArray(r?.disabled_templates) ? r.disabled_templates : []))
            .catch(() => {});
        // iter63z (template-visibility) — Fetch per-template visibility
        // overrides set by Site Admin. Silently no-ops on failure so the
        // hardcoded defaults still drive the picker.
        api.getTemplateVisibility()
            .then((r) => setVisibilityOverrides(r?.overrides && typeof r.overrides === "object" ? r.overrides : {}))
            .catch(() => {});
        // iter86.31.6 — Curated club-season bindings drive the Club Season
        // league dropdown. On failure keep null → no filtering (builtins).
        api.getClubSeasonBindings()
            .then((d) => setClubBindings(d?.bindings || null))
            .catch(() => {});
        // iter86.45 — Golf event bindings for the consolidated golf card.
        api.getGolfBindings()
            .then((d) => setGolfBindings(d?.bindings || []))
            .catch(() => setGolfBindings([]));
        // iter86.74 — Global custom-feature availability drives the wizard's
        // data-feed options + Portfolio Draft toggle. Failure → all enabled.
        api.getSiteSettings()
            .then((s) => setCustomFeaturesDisabled(Array.isArray(s?.disabled_custom_features) ? s.disabled_custom_features : []))
            .catch(() => {});
    }, [leagueId, navigate, initialTeamsPerParam]);

    // iter86.74 — Pre-check Portfolio Draft from the rivalry's current flag.
    useEffect(() => {
        if (league) setPortfolioUnlock(!!league.portfolio_mode_unlocked);
    }, [league]);

    const isCustom = template === "custom";

    const addTeamRow = () => setCustomTeams([...customTeams, { name: "", group: format === "group_knockout" ? "A" : "" }]);
    const removeTeamRow = (i) => setCustomTeams(customTeams.filter((_, idx) => idx !== i));
    const updateTeam = (i, field, value) => {
        const updated = [...customTeams];
        updated[i][field] = value;
        setCustomTeams(updated);
    };

    // iter84.4 — Non-manual feeds gate behind the Terms-of-Use modal;
    // the radio only flips once the admin has accepted for that feed.
    const handleSelectDataSource = (key, needsTerms) => {
        if (needsTerms && !termsAcceptedFor[key]) {
            setPendingDataSource(key);
            setTermsModalOpen(true);
            return;
        }
        setDataSource(key);
    };

    // When format changes, reset/clear group field on rows where it doesn't apply
    const handleFormatChange = (newFmt) => {
        setFormat(newFmt);
        if (newFmt !== "group_knockout") {
            setCustomTeams(customTeams.map(t => ({ ...t, group: "" })));
        } else {
            setCustomTeams(customTeams.map(t => ({ ...t, group: t.group || "A" })));
        }
    };

    const handleCreate = async () => {
        if (!tournamentName.trim()) {
            toast.error("Tournament needs a name");
            return;
        }
        // iter87.28 — Explicit template gate. Prevents the silent
        // "no card selected → default World Cup" behavior.
        if (!template) {
            toast.error("Pick a template above");
            return;
        }
        // iter87.554 — Squares now requires the real game to be picked
        // IN the wizard (no post-creation admin tab to attach it in
        // anymore), so this guard replaces what used to be an
        // open-ended admin-tab reminder.
        if (template.startsWith("nfl_squares_") || template.startsWith("cfb_squares_")) {
            if (!squaresGameId) {
                toast.error("Pick the real game for this Squares board");
                return;
            }
        }
        // iter84.9 — Custom tournaments now require start + end dates
        // (drives the auto-sync poller window). Templates use their own
        // baked-in dates, so the check applies only to is_custom.
        if (isCustom) {
            if (!startDate || !endDate) {
                toast.error("Pick start + end dates for the tournament");
                return;
            }
            if (startDate > endDate) {
                toast.error("Start date must be on or before end date");
                return;
            }
        }
        setLoading(true);
        try {
            const payload = { league_id: leagueId, name: tournamentName.trim(), template };
            // iter86.31.6 — Dynamic club league → generic template + binding id.
            if (template.startsWith("dyn:")) {
                payload.template = "club_season_api";
                payload.club_binding_id = template.slice(4);
            }
            // iter86.41 — Pot Pick'em league → league template + pickem flag.
            if (template.startsWith("pickem:")) {
                payload.template = template.slice(7);
                payload.pickem_mode = "pot";
            }
            // iter86.45 — Club templates: league keys, dyn bindings, pickem.
            const isClubTpl = template.startsWith("pickem:") || template.startsWith("dyn:") ||
                (TEMPLATE_PRESETS.find((t) => t.key === "club_season")?.leagues || []).some((l) => l.key === template);
            if (isClubTpl && seasonWindow !== "full") {
                payload.season_window = seasonWindow;
            }
            // iter87.199.178 — Matchday Boost / Weekly Spread Challenge
            // opt-out checkbox removed from the wizard; both always stay
            // ON (server default) at creation time.
            // iter87.199.79 → iter87.199.162 — Weekly picks-per-round
            // passes through the wizard (generic field, shared by NFL
            // Weekly + the new EPL/La Liga Weekly Spread templates).
            if (["nfl_boost_api", "epl_boost_api", "la_liga_boost_api"].includes(payload.template)) {
                if (Number.isInteger(nflPicksPerRound) && nflPicksPerRound > 0) {
                    payload.nfl_picks_per_round = nflPicksPerRound;
                }
            }
            // iter86.45 — Golf event binding (curated; null = platform default).
            if (template.startsWith("golf_") && golfBindingId) {
                payload.golf_binding_id = golfBindingId;
            }
            // iter87.213.7 — MLB Playoffs is pot-pick'em only (no draft
            // alternative), same bug pattern iter87.199.67 already fixed
            // for NFL/club leagues: without pickem_mode="pot" the backend
            // skips the whole pot_rules/power_pick_multipliers/team_seeds
            // setup block AND the email copy falls back to the wrong
            // "auto_draft" family text.
            if (template === "mlb_playoffs_api") {
                payload.pickem_mode = "pot";
            }
            // iter87.225 — NBA Playoffs, sibling of the MLB branch above.
            if (template === "nba_playoffs_api") {
                payload.pickem_mode = "pot";
            }
            // iter87.230 → iter87.374 → iter87.437 — CFB Playoffs
            // followed NFL Playoffs' lead (iter87.374): pure pick'em
            // (National Champion + Dark Horse Pick, no draft/pot), so
            // it no longer sends `pickem_mode` either — mirrors NWSL
            // Playoffs, which never sent it in the first place.
            if (isCustom) {
                // iter86.74 — Persist the league admin's feature choices as
                // rivalry-wide flags (additive: picking a feed enables it;
                // other feeds are left untouched). 403 = globally disabled.
                const feat = { portfolio_mode_unlocked: portfolioUnlock };
                if (dataSource === "gotsport") feat.gotsport_enabled = true;
                if (dataSource === "csv") feat.feed_csv_enabled = true;
                await api.leagueSetCustomFeatures(leagueId, feat);
                payload.data_source = dataSource;
                payload.format = format;
                payload.start_date = startDate;
                payload.end_date = endDate;
                if (format === "knockout_only") payload.knockout_size = knockoutSize;
                // iter84.9 — Teams are entered in the wizard ONLY when
                // data_source = manual. For external feeds the teams sync
                // in post-creation, so skip the team-validation block
                // entirely and let the backend stub the roster.
                if (dataSource === "manual") {
                    const cleanTeams = customTeams
                        .filter(t => t.name.trim())
                        .map((t, idx) => {
                            const out = { name: t.name.trim(), group: t.group?.trim() || null };
                            if (format === "knockout_only") {
                                out.seed = idx + 1;
                            } else {
                                const s = parseInt(t.seed, 10);
                                if (Number.isInteger(s) && s > 0) out.seed = s;
                            }
                            return out;
                        });
                    if (cleanTeams.length < 2) {
                        toast.error("Add at least 2 teams");
                        setLoading(false);
                        return;
                    }
                    if (format === "knockout_only") {
                        const minTeams = Math.floor(knockoutSize / 2) + 1;
                        if (cleanTeams.length > knockoutSize) {
                            toast.error(`A ${knockoutSize}-seed bracket can't have more than ${knockoutSize} teams (you've added ${cleanTeams.length}).`);
                            setLoading(false);
                            return;
                        }
                        if (cleanTeams.length < minTeams) {
                            toast.error(`A ${knockoutSize}-seed bracket needs at least ${minTeams} teams (top seeds get byes if you have fewer than ${knockoutSize}). You've added ${cleanTeams.length}.`);
                            setLoading(false);
                            return;
                        }
                    }
                    payload.teams = cleanTeams;
                } else {
                    // External feed — minimal stub roster so the backend's
                    // 2-team minimum passes; the real teams come in via
                    // the Data Feed sync after creation.
                    payload.teams = [
                        { name: "Team 1 (will sync)", group: null },
                        { name: "Team 2 (will sync)", group: null },
                    ];
                }
            }
            const created = await api.createTournament(payload);
            // iter87.554 — attach the wizard-picked game immediately after
            // creation (reuses the existing, already-tested PUT endpoint —
            // no new creation-time backend plumbing needed).
            if (created?.id && squaresGameId) {
                try {
                    await api.squaresPutGame(created.id, squaresGameId);
                } catch (gameErr) {
                    toast.error(gameErr.response?.data?.detail || "Tournament created, but attaching the game failed — attach it from Trophy Tokens.");
                }
            }
            // iter84.9 — Teams-per-player picker was removed from the
            // wizard (lives on the Draft Setup card now). The league
            // default carries over unchanged.
            // iter86.35 — Land on the tournament that was just set up, not
            // the league default: store it as this admin's viewed
            // tournament (per-user localStorage override the dashboard
            // reads on mount). League-wide default is unchanged.
            if (created?.id) setStoredViewTournament(leagueId, created.id);
            toast.success("Tournament created!");
            navigate(`/league/${leagueId}`);
        } catch (err) {
            const detail = err.response?.data?.detail;
            // iter72 — Free Rivalry cap reached → open the trial-code upgrade modal
            // instead of a generic toast. Backend returns a structured payload
            // with upgrade_via_trial_code=true.
            if (typeof detail === "object" && ["tournament_cap_reached", "free_tier_completed"].includes(detail?.error) && detail?.upgrade_via_trial_code) {
                setUpgradeMessage(detail.message || "Have a trial code? Redeem it to unlock more Tournaments.");
                setUpgradeModalOpen(true);
            } else {
                toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed"));
            }
        }
        setLoading(false);
    };

    // iter72 — Self-redeem trial code → flip the Rivalry to comp tier.
    const handleRedeemTrialCode = async (code) => {
        try {
            await api.redeemTrialCode(leagueId, code);
            toast.success("Trial code redeemed — your Rivalry is upgraded. Try creating the Tournament again.");
            setUpgradeModalOpen(false);
            // Re-fetch the league so the cap state in the parent flow is fresh.
            api.getLeague(leagueId).then((d) => setLeague(d.league)).catch(() => {});
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to redeem code");
        }
    };

    return (
        <Takeover
            open
            onClose={() => navigate(`/league/${leagueId}`)}
            title="Create Tournament"
            testId="create-tournament-takeover"
            maxWidthClass="lg:max-w-3xl"
            icon={Trophy}
            iconTone="cyber"
            actionBar={
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-[11px] text-zinc-500 flex items-center gap-2 min-w-0">
                        <Settings className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Edit scoring, teams, and matches after creating.</span>
                    </div>
                    <button
                        data-testid="create-tournament-btn"
                        onClick={handleCreate}
                        disabled={loading || !template || squaresNeedsGame}
                        title={!template ? "Pick a template above first" : squaresNeedsGame ? "Pick the real game above first" : undefined}
                        className="inline-flex items-center gap-2 rounded-full bg-cyber text-black font-black uppercase tracking-widest text-xs px-4 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
                    >
                        <Trophy className="w-4 h-4" strokeWidth={2.5} />
                        {loading ? "Creating…" : "Create Tournament"}
                    </button>
                </div>
            }
        >
            <div className="max-w-2xl mx-auto space-y-5">
                {league?.name && (
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-1">{league.name}</p>
                )}

                <div className="rounded-2xl border border-white/[0.08] bg-[#121212] p-4 space-y-4">
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2">Tournament name</label>
                        <input
                            data-testid="tournament-name-input"
                            value={tournamentName}
                            onChange={(e) => setTournamentName(e.target.value)}
                            placeholder="2026 World Cup Pool"
                            className="w-full rounded-xl bg-[#0e0e0e] border border-white/[0.10] focus:border-cyber/60 focus:outline-none px-3 py-2.5 text-base text-white placeholder:text-zinc-600"
                        />
                    </div>

                    <TemplatePicker
                        template={template}
                        setTemplate={setTemplate}
                        seasonWindow={seasonWindow}
                        setSeasonWindow={setSeasonWindow}
                        golfBindingId={golfBindingId}
                        setGolfBindingId={setGolfBindingId}
                        squaresGames={squaresGames}
                        squaresGamesLoading={squaresGamesLoading}
                        squaresGameId={squaresGameId}
                        setSquaresGameId={setSquaresGameId}
                        visibilityOverrides={visibilityOverrides}
                        disabledTemplates={disabledTemplates}
                        clubBindings={clubBindings}
                        golfBindings={golfBindings}
                        nflPicksPerRound={nflPicksPerRound}
                        setNflPicksPerRound={setNflPicksPerRound}
                        customEnabled={!!league?.custom_enabled}
                        proPlusOk={!!league?.pro_plus_ok}
                    />
                    {/* iter62d — Disclaimer (nominative fair use). */}
                    <p
                        data-testid="template-affiliation-disclaimer"
                        className="text-[10px] text-zinc-500 leading-relaxed"
                    >
                        Tournament names are descriptive labels for publicly known event structures.
                        <strong className="text-zinc-400"> Not affiliated with FIFA, UEFA, the NCAA, the NFL, the PGA Tour, the DP World Tour, the IOC, or any other sports organization.</strong>
                        {" "}Trademarks belong to their respective owners.
                    </p>
                </div>

                {isCustom && (
                    <div className="rounded-2xl border border-white/[0.08] bg-[#121212] p-4">
                        <CustomTournamentFields
                            league={league}
                            customFeaturesDisabled={customFeaturesDisabled}
                            portfolioUnlock={portfolioUnlock}
                            setPortfolioUnlock={setPortfolioUnlock}
                            dataSource={dataSource}
                            onSelectDataSource={handleSelectDataSource}
                            termsAcceptedFor={termsAcceptedFor}
                            format={format}
                            onFormatChange={handleFormatChange}
                            knockoutSize={knockoutSize}
                            setKnockoutSize={setKnockoutSize}
                            customTeams={customTeams}
                            updateTeam={updateTeam}
                            addTeamRow={addTeamRow}
                            removeTeamRow={removeTeamRow}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            dateRangeDays={dateRangeDays}
                        />
                    </div>
                )}

                {/* iter84.9 — Teams Per Player picker removed from the
                    create wizard. Lives on the Draft Setup card after
                    creation; surfacing it during initial setup was
                    confusing first-time admins about whether it locks
                    in pre-draft. */}
            </div>

            {/* iter72 — Trial code upgrade modal */}
            {upgradeModalOpen && (
                <RedeemTrialCodeModal
                    message={upgradeMessage}
                    onClose={() => setUpgradeModalOpen(false)}
                    onRedeem={handleRedeemTrialCode}
                />
            )}

            {/* iter84.4 (Phase 2) — Data Feed Terms-of-Use modal. Shown
                when the rivalry admin selects a non-manual data feed. They
                must accept before the radio is updated. */}
            {termsModalOpen && pendingDataSource && DATA_SOURCE_TERMS[pendingDataSource] && (
                <DataFeedTermsModal
                    feedKey={pendingDataSource}
                    terms={DATA_SOURCE_TERMS[pendingDataSource]}
                    onClose={() => { setTermsModalOpen(false); setPendingDataSource(null); }}
                    onAccept={() => {
                        setTermsAcceptedFor({ ...termsAcceptedFor, [pendingDataSource]: true });
                        setDataSource(pendingDataSource);
                        setTermsModalOpen(false);
                        setPendingDataSource(null);
                    }}
                />
            )}
        </Takeover>
    );
}

